import React, { useMemo } from 'react';
import { styled } from '@cx/ui/theme';
import Text from '@cx/ui/components/Text';
import Tags, { TagsProps, TagType } from '@cx/ui/components/Tags';
import { FilterBarValue, TagTypes } from 'types';
import { TagVariant } from '@cx/ui/components/Tag';

export { TagVariant };

export type FilterHintType = TagType & FilterBarValue;

export type CreateLabelType = (params: {
  filter: FilterBarValue;
  value: string;
}) => string;

export type AdapFilterHintProps = (params: {
  filter: FilterBarValue;
  value: string;
}) => Partial<FilterHintType> | null;

export type AdaptFilterBarValuesType = (params: {
  filterBarValues: FilterHintType[];
  createLabel?: CreateLabelType;
  adaptFilterHintProps?: AdapFilterHintProps;
}) => TagType[];

const createDefaultLabel = ({
  filter,
  value,
}: {
  filter: FilterBarValue;
  value: string;
}) => {
  const Label = filter.DisplayName || filter.AttributeName;
  if (filter.Tag === TagTypes.Advanced) {
    return `${Label} ${filter.SubAttribute} ${filter.OpType} ${value}`;
  }
  if (filter.Tag === ('Weight' as TagTypes)) {
    return `${value}`;
  }
  return `${Label}: ${value}`;
};

/**
 * Helper function that adapts the filter bar values to match the tags component format.
 * @param {FilterBarValue} filterBarValues
 * @returns {TagType}
 */

const defaultAdaptFilterBarValues: AdaptFilterBarValuesType = ({
  filterBarValues,
  createLabel = createDefaultLabel,
  adaptFilterHintProps,
}): TagType[] => {
  return filterBarValues.flatMap((filter) => {
    const Label = filter.DisplayName || filter.AttributeName;
    if (filter.Tag === TagTypes.Date) {
      const value = filter.Values.join(' - ');
      return {
        id: `${Label}-${filter.Tag}-${value}`,
        label: createLabel({
          filter,
          value,
        }),
        filter,
        value,
        ...(adaptFilterHintProps?.({
          filter,
          value,
        }) || {}),
      };
    }
    return filter.Values.flatMap((value) => ({
      id: `${Label}-${filter.Tag}-${value}`,
      label: createLabel({
        filter,
        value,
      }),
      filter,
      value,
      ...(adaptFilterHintProps?.({
        filter,
        value,
      }) || {}),
    }));
  });
};

export type FilterHintsProps = Omit<TagsProps, 'tags'> &
  React.HTMLAttributes<HTMLDivElement> & {
    filters: FilterHintType[];
    title?: string;
    createLabel?: CreateLabelType;
    adaptFilterBarValues?: AdaptFilterBarValuesType;
    adaptFilterHintProps?: AdapFilterHintProps;
  };

/**
 * @description component that renders the filter hints. It receives the filters and renders them as tags.
 * @param {FilterHintsProps} props - The props of the component. Same as the Tags component with the addition of the filters prop.
 * @returns {React.ReactElement}
 */
function FilterHints({
  filters,
  title = 'Filtered by:',
  className,
  createLabel = createDefaultLabel,
  adaptFilterBarValues = defaultAdaptFilterBarValues,
  adaptFilterHintProps,
  ...props
}: FilterHintsProps): React.ReactElement {
  const tags = useMemo(() => {
    if (!filters || !filters.length) {
      return [];
    }
    return adaptFilterBarValues?.({
      filterBarValues: filters,
      createLabel,
      adaptFilterHintProps,
    });
  }, [filters]);

  return (
    <div className={className}>
      <Text className="cx-filter-hints-title">{title}</Text>
      <Tags {...props} tags={tags || []} />
    </div>
  );
}

const StyledFiltersHints = styled(FilterHints)(({ theme }) => ({
  '.cx-filter-hints-title': {
    ...theme.typography.small,
    marginBottom: theme.spacing(0.75),
  },
}));

export { StyledFiltersHints as FilterHints };
